// ----------------------------------------------------------------
// <copyright company='Microsoft Corporation'>
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
// ----------------------------------------------------------------

const EXCLUDED_REQUEST_HEADERS = ['AUTHORIZATION'];

/**
 * Recursively removes authorization headers from an object and sanitizes Bearer tokens in strings
 * @param obj - The object to sanitize
 * @param seen - WeakSet to track visited objects and prevent infinite loops
 * @returns The sanitized object (mutates in place)
 */
export function removeAuthHeaders(obj: any, seen = new WeakSet()): any {
    try
    {
        if (!obj || typeof obj !== 'object') {
            // Sanitize Bearer tokens in strings
            if (typeof obj === 'string' && /Bearer\s+[\w\-.\+\/=]+/i.test(obj)) {
                return obj.replace(/Bearer\s+[\w\-.\+\/=]+/gi, '[REDACTED]');
            }
            return obj;
        }

        // Prevent infinite loops from circular references
        if (seen.has(obj)) {
            return obj;
        }
        seen.add(obj);

        // Handle arrays
        if (Array.isArray(obj)) {
            obj.forEach((item, index) => {
                obj[index] = removeAuthHeaders(item, seen);
            });
            return obj;
        }
        
        // Handle objects
        for (const key in obj) {
            if (obj.hasOwnProperty(key)) {
                // Remove authorization headers at any level
                if (EXCLUDED_REQUEST_HEADERS.includes(key.toUpperCase())) {
                    obj[key] = '[REDACTED]';
                } else if (typeof obj[key] !== 'function') {
                    // Recursively sanitize nested objects
                    obj[key] = removeAuthHeaders(obj[key], seen);
                }
            }
        }
        return obj;
    } catch (error) {
        return obj;
    }
}